import PropTypes from 'prop-types'
import React, { cloneElement, useEffect, useRef, useState } from 'react'
import { Button, splitData } from './Button'

// Controlled: value is the saved value. The draft is the cluster's own until
// onSave's request succeeds and the parent passes the saved value back.
export function InlineEdit({ value, onSave, onFailure, children, ...props }) {
  const [feedback, rest] = splitData(props)
  const [draft, setDraft] = useState(value)
  const [editing, setEditing] = useState(false)
  const [pending, setPending] = useState(false)
  const field = useRef(null)
  const save = useRef(null)
  const cluster = useRef(null)
  useEffect(() => {
    setDraft(value)
  }, [value])
  // A save that settles after focus left the cluster closes it.
  useEffect(() => {
    if (
      !pending &&
      draft === value &&
      !cluster.current.contains(document.activeElement)
    )
      setEditing(false)
  }, [pending, draft, value])
  const cancel = () => {
    if (pending) return
    setDraft(value)
    field.current.focus()
    setEditing(false)
  }
  return (
    <span
      {...rest}
      ref={cluster}
      className={`me-btn-inline-edit-cluster${editing ? ' is-editing' : ''}`}
      data-me-react=""
      onFocus={() => setEditing(true)}
      onBlur={event => {
        if (
          !event.currentTarget.contains(event.relatedTarget) &&
          draft === value
        )
          setEditing(false)
      }}
    >
      {cloneElement(children, {
        ref: node => {
          field.current = node
          if (typeof children.ref === 'function') children.ref(node)
          else if (children.ref) children.ref.current = node
        },
        value: draft,
        readOnly: pending || children.props.readOnly,
        onChange: event => {
          setDraft(event.target.value)
          setEditing(true)
          if (children.props.onChange) children.props.onChange(event)
        },
        onKeyDown: event => {
          if (children.props.onKeyDown) children.props.onKeyDown(event)
          if (event.defaultPrevented) return
          // A key that ends an IME composition belongs to the composition;
          // Safari marks it only with keyCode 229.
          if (event.nativeEvent.isComposing || event.keyCode === 229) return
          if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
            cancel()
          }
          // Enter in a textarea is a line break.
          if (event.key === 'Enter' && event.target.matches('input')) {
            event.preventDefault()
            if (!pending) save.current.click()
          }
        },
      })}
      <Button
        {...feedback}
        ref={save}
        className="me-btn-icon me-btn-icon-primary me-btn-icon-sm"
        icon="fa-regular fa-floppy-disk"
        aria-label={window.MEButtons.text('strSave')}
        data-tooltip={window.MEButtons.text('strSave')}
        data-edit="save"
        data-load="success"
        onAction={() => {
          setPending(true)
          return onSave(draft)
        }}
        onSuccess={() => setPending(false)}
        onFailure={error => {
          setPending(false)
          onFailure?.(error)
        }}
        onCancel={() => setPending(false)}
      />
      <Button
        className="me-btn-icon me-btn-icon-sm"
        icon="fa-regular fa-xmark"
        aria-label={window.MEButtons.text('strCancel')}
        data-tooltip={window.MEButtons.text('strCancel')}
        data-edit="cancel"
        aria-disabled={pending || undefined}
        onClick={cancel}
      />
    </span>
  )
}

InlineEdit.propTypes = {
  value: PropTypes.string.isRequired,
  // Receives the draft and returns the request's promise.
  onSave: PropTypes.func.isRequired,
  onFailure: PropTypes.func,
  // The single text input or textarea the cluster edits.
  children: PropTypes.element.isRequired,
}
